/**
 * In-browser fake of `/api/v1/ai/menu-round` with the same rules as api/app/ai/menu_round.py (roles, proposal,
 * pending edit, drift, expiry). Mock-only switches (read on every call, like `cf-chat.kb-role`):
 *   - viewer role: localStorage `cf-chat.kb-role` = owner | head | staff (default owner)
 *   - scenario:    localStorage `cf-chat.menu-mock` = none | active | drift | expiring | expired | proposal | posdown
 *                  (default none). Changing it re-seeds; while it stays the same, approvals/edits are kept.
 * The dishes are drawings + placeholder numbers for the layout only — the real ones come from POS.
 */
import { ApiError } from '../client'
import { createMockMedia } from '../mockLibrary'
import type { MediaAsset } from '../types'
import {
  addDaysYmd,
  addMonthsYmd,
  bangkokToday,
  DEFAULT_FIELDS,
  diffDays,
  FIELD_KEYS,
  HIDE_DISH,
  periodProblem,
  toRoundDish,
} from '../../lib/menuRound'
import type { KbRole } from './types'
import type {
  DishValues,
  EditBody,
  LiveDish,
  MenuAction,
  MenuAlert,
  MenuDrift,
  MenuNotes,
  MenuPending,
  MenuRound,
  MenuRoundApi,
  MenuRoundRowStatus,
  MenuRoundState,
  MenuRoundStatus,
  NewRoundBody,
  OverrideMap,
} from './menuTypes'

export const MENU_MOCK_KEY = 'cf-chat.menu-mock'
export const MENU_ROLE_KEY = 'cf-chat.kb-role'
export const MOCK_POSTER_ID = 'md6'
export type MenuScenario = 'none' | 'active' | 'drift' | 'expiring' | 'expired' | 'proposal' | 'posdown'
const SCENARIOS: MenuScenario[] = ['none', 'active', 'drift', 'expiring', 'expired', 'proposal', 'posdown']

const PEOPLE: Record<KbRole, { id: string; name: string }> = {
  owner: { id: '1', name: 'เจ้าของร้าน' },
  head: { id: '2', name: 'ผู้จัดการ' },
  staff: { id: '3', name: 'พลอย' },
}

export const FLAG_LABELS: Record<string, string> = {
  garlic_free: 'ปลอดกระเทียม',
  wheat: 'มีแป้งสาลี',
  seafood: 'มีอาหารทะเล',
  egg: 'มีไข่',
  milk: 'มีนม',
  peanut: 'มีถั่วลิสง',
  soy: 'มีถั่วเหลือง',
  vegan: 'วีแกน',
}
const KIND_LABEL: Record<string, string> = { chicken: 'ไก่', pork: 'หมู', beef: 'เนื้อ', fish: 'ปลา', seafood: 'ทะเล', pasta: 'เส้น', other: 'อื่นๆ' }
const KIND_COLOR: Record<string, [string, string]> = {
  chicken: ['#f1d38a', '#d98e5f'],
  pork: ['#e7b76a', '#c9795c'],
  beef: ['#c9795c', '#8f5a3c'],
  fish: ['#f3c7a0', '#e9b949'],
  seafood: ['#f2a38a', '#e07b5f'],
  pasta: ['#f5e0a0', '#e9b949'],
  other: ['#c7dc8a', '#9ccf7c'],
}

/** A plate drawing standing in for the POS photo. */
function plate(kind: string, i: number): string {
  const [a, b] = KIND_COLOR[kind] ?? KIND_COLOR.other!
  const g = ['#9ccf7c', '#7fb58f', '#b9d98f'][i % 3]
  const body =
    `<rect width="96" height="96" fill="#f4f1ea"/><circle cx="48" cy="48" r="38" fill="#fff"/><circle cx="48" cy="48" r="30" fill="#f7f7f4"/>` +
    `<ellipse cx="40" cy="50" rx="17" ry="13" fill="${a}"/><ellipse cx="57" cy="44" rx="12" ry="10" fill="${b}"/>` +
    `<circle cx="56" cy="60" r="7" fill="${g}"/><circle cx="36" cy="37" r="5" fill="${g}"/>`
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">${body}</svg>`)
}

type Nums = [number | null, number | null, number | null, number | null, number | null, number | null]
type Seed = [id: string, code: string, name: string, kind: string, spicy: string | null, flags: string[], desc: string | null, ingr: string | null, n: Nums]

const LONG_DESC =
  'อกไก่หมักซอสเทริยากิสูตรลดน้ำตาล ย่างจนหอม เสิร์ฟกับข้าวกล้องหอมมะลิและผักลวกสามอย่าง บรอกโคลี แครอท และข้าวโพดอ่อน ' +
  'รสชาติกลมกล่อมไม่หวานจัด เหมาะกับคนที่เพิ่งเริ่มทานอาหารคลีน อุ่นไมโครเวฟแล้วเนื้อไก่ยังนุ่ม ไม่แห้ง'

const SEEDS: Seed[] = [
  ['101', 'เพรา', 'กะเพราไก่สับ', 'chicken', 'เผ็ดกลาง', ['soy'], 'ไก่สับผัดกะเพรารสจัดจ้าน ใช้น้ำมันน้อย เสิร์ฟกับข้าวกล้อง', 'อกไก่สับ ใบกะเพรา พริก กระเทียม ซอสถั่วเหลือง ข้าวกล้อง', [320, 33, 34, 7, 2, 230]],
  ['105', 'จัง', 'ไก่ย่างโคชูจัง', 'chicken', 'เผ็ดน้อย', ['soy', 'wheat'], 'ไก่ย่างซอสโคชูจังเกาหลี หวานเผ็ดนิดๆ', 'สะโพกไก่ ซอสโคชูจัง งาขาว ข้าวไรซ์เบอร์รี่', [360, 30, 38, 10, 6, 240]],
  ['106', 'เทียม', 'ไก่สับกระเทียมพริกไทย', 'chicken', 'ไม่เผ็ด', ['soy'], 'ไก่สับผัดกระเทียมพริกไทย หอมกระเทียมเจียว', 'อกไก่สับ กระเทียม พริกไทย ซอสปรุงรส ข้าวกล้อง', [310, 32, 33, 8, 1, 230]],
  ['181', 'เขียว', 'แกงเขียวหวานไก่', 'chicken', 'เผ็ดกลาง', [], 'แกงเขียวหวานกะทิน้อย เนื้อไก่นุ่ม', 'อกไก่ พริกแกงเขียวหวาน กะทิ มะเขือเปราะ ใบโหระพา', [380, 29, 30, 15, 4, 250]],
  ['201', 'เทริ', 'อกไก่ย่างซอสเทริยากิ', 'chicken', 'ไม่เผ็ด', ['soy', 'wheat'], LONG_DESC, 'อกไก่ ซอสเทริยากิ ข้าวกล้อง บรอกโคลี แครอท ข้าวโพดอ่อน', [180, 33, 4, 3, 1, 150]],
  ['202', 'มะม่วง', 'ไก่ผัดเม็ดมะม่วง', 'chicken', 'ไม่เผ็ด', ['soy', 'garlic_free'], 'ไก่ผัดเม็ดมะม่วงหิมพานต์ ไม่ใส่กระเทียม', 'อกไก่ เม็ดมะม่วงหิมพานต์ หอมใหญ่ พริกหวาน ซอสหอยนางรม', [390, 31, 32, 14, 5, 240]],
  ['203', 'กะเพราข้าว', 'ข้าวกล้องผัดกะเพราอกไก่', 'chicken', 'เผ็ดกลาง', ['soy'], 'ข้าวกล้องผัดกะเพราอกไก่ หอมใบกะเพรา', 'ข้าวกล้อง อกไก่ ใบกะเพรา พริก กระเทียม', [410, 30, 48, 9, 2, 250]],
  ['204', 'สมุนไพร', 'อกไก่ย่างสมุนไพร', 'chicken', 'ไม่เผ็ด', ['garlic_free'], 'อกไก่ย่างสมุนไพรแบบตะวันตก เสิร์ฟกับมันหวาน', 'อกไก่ โรสแมรี่ ไทม์ มันหวาน ถั่วลันเตา', [300, 35, 25, 6, null, 220]],
  ['205', 'เกลือ', 'ไก่คั่วพริกเกลือ', 'chicken', 'เผ็ดน้อย', [], 'ไก่คั่วพริกเกลือ กรอบนอกนุ่มใน', 'อกไก่ พริก เกลือ กระเทียม ต้นหอม', [340, 32, 28, 11, 1, 230]],
  ['206', 'พริกแกง', 'ไก่ผัดพริกแกง', 'chicken', 'เผ็ดมาก', [], null, null, [350, 30, 30, 12, 3, 230]],
  ['207', 'ลาบ', 'ลาบไก่', 'chicken', 'เผ็ดกลาง', ['garlic_free'], 'ลาบไก่สับ ข้าวคั่วหอม เสิร์ฟกับผักสด', 'อกไก่สับ ข้าวคั่ว หอมแดง สะระแหน่ ผักสด', [280, 34, 18, 7, 1, 220]],
  ['208', 'BBQ', 'ไก่อบซอสบาร์บีคิว', 'chicken', 'ไม่เผ็ด', ['wheat'], 'ไก่อบซอสบาร์บีคิวสูตรน้ำตาลน้อย', 'สะโพกไก่ ซอสบาร์บีคิว มันฝรั่ง ข้าวโพด', [400, 29, 40, 13, 7, 250]],
  ['209', 'ข่า', 'ต้มข่าไก่', 'chicken', 'เผ็ดน้อย', [], 'ต้มข่าไก่กะทิน้อย เปรี้ยวหอมมะนาว', 'อกไก่ ข่า ตะไคร้ ใบมะกรูด เห็ด กะทิ', [260, 26, 12, 12, 3, 280]],
  ['210', 'ขิง', 'ไก่ผัดขิง', 'chicken', 'ไม่เผ็ด', ['soy'], 'ไก่ผัดขิงอ่อน เห็ดหูหนู', 'อกไก่ ขิงอ่อน เห็ดหูหนู ต้นหอม ซอสถั่วเหลือง', [300, 31, 30, 8, 2, 230]],
  ['250', 'หมูดำ', 'สเต๊กหมูพริกไทยดำ', 'pork', 'ไม่เผ็ด', ['wheat', 'soy'], 'สันในหมูย่างราดซอสพริกไทยดำ เสิร์ฟกับผักย่าง', 'สันในหมู ซอสพริกไทยดำ ฟักทอง บรอกโคลี', [380, 32, 22, 16, 4, 230]],
  ['251', 'หยวก', 'หมูผัดพริกหยวก', 'pork', 'เผ็ดน้อย', ['soy'], 'หมูผัดพริกหยวก หอมกระเทียม', 'สันในหมู พริกหยวก กระเทียม ซอสหอยนางรม', [350, 28, 30, 13, 3, 230]],
  ['252', 'ผักรวม', 'หมูสับผัดผักรวม', 'pork', 'ไม่เผ็ด', ['soy'], 'หมูสับผัดผักรวมหลากสี', 'หมูสับ บรอกโคลี แครอท ข้าวโพดอ่อน ข้าวกล้อง', [330, 27, 34, 10, 3, 240]],
  ['253', 'แจ่ว', 'หมูย่างจิ้มแจ่ว', 'pork', 'เผ็ดกลาง', ['garlic_free'], 'หมูย่างเนื้อนุ่ม น้ำจิ้มแจ่วรสจัด', 'สันคอหมู น้ำจิ้มแจ่ว ข้าวเหนียวดำ ผักสด', [420, 30, 36, 16, 3, 240]],
  ['254', 'กระเทียม', 'หมูผัดกระเทียม', 'pork', 'ไม่เผ็ด', ['soy'], 'หมูผัดกระเทียมพริกไทย ทานง่าย', 'สันในหมู กระเทียม พริกไทย ข้าวกล้อง', [360, 29, 32, 13, 1, 230]],
  ['255', 'เห็ด', 'หมูอบซอสเห็ด', 'pork', 'ไม่เผ็ด', ['milk', 'wheat'], 'หมูอบราดซอสเห็ดครีมเบา', 'สันในหมู เห็ดแชมปิญอง นม มันบด', [410, 30, 28, 18, 5, 240]],
  ['270', 'หอย', 'เนื้อผัดน้ำมันหอย', 'beef', 'ไม่เผ็ด', ['soy'], 'เนื้อวัวผัดน้ำมันหอย คะน้าฮ่องกง', 'เนื้อวัว น้ำมันหอย คะน้า ข้าวกล้อง', [390, 31, 30, 15, 4, 230]],
  ['271', 'สเต๊กเนื้อ', 'สเต๊กเนื้อพริกไทยดำ', 'beef', 'ไม่เผ็ด', ['wheat'], 'สเต๊กเนื้อวัวย่างสุกกำลังดี ซอสพริกไทยดำ', 'เนื้อวัว ซอสพริกไทยดำ มันอบ สลัด', [450, 36, 24, 20, 3, 240]],
  ['272', 'พริกไทยอ่อน', 'เนื้อผัดพริกไทยอ่อน', 'beef', 'เผ็ดกลาง', [], 'เนื้อผัดพริกไทยอ่อน กระชาย', 'เนื้อวัว พริกไทยอ่อน กระชาย ใบโหระพา', [370, 32, 24, 16, 2, 230]],
  ['237', 'แซล', 'แซลมอนย่างซีอิ๊วญี่ปุ่น', 'fish', 'ไม่เผ็ด', ['soy', 'wheat'], 'แซลมอนย่างซอสซีอิ๊วญี่ปุ่น เสิร์ฟกับข้าวญี่ปุ่นผสมธัญพืช', 'แซลมอน ซีอิ๊วญี่ปุ่น ข้าวญี่ปุ่น ธัญพืช ผักโขม', [420, 30, 32, 18, 4, 220]],
  ['240', 'ดอลลี่', 'ปลาดอลลี่นึ่งมะนาว', 'fish', 'เผ็ดกลาง', ['garlic_free'], 'ปลาดอลลี่นึ่งมะนาว น้ำยำเปรี้ยวจี๊ด', 'ปลาดอลลี่ มะนาว พริก ผักกาดขาว', [220, 28, 12, 6, 2, 240]],
  ['241', 'ขึ้นฉ่าย', 'ปลาดอลลี่ผัดขึ้นฉ่าย', 'fish', 'ไม่เผ็ด', ['soy'], 'ปลาดอลลี่ผัดขึ้นฉ่าย เต้าเจี้ยว', 'ปลาดอลลี่ ขึ้นฉ่าย เต้าเจี้ยว ขิง', [260, 26, 20, 8, 2, 230]],
  ['242', 'แซลดำ', 'ปลาแซลมอนผัดพริกไทยดำ', 'fish', 'ไม่เผ็ด', ['wheat', 'soy'], 'แซลมอนผัดพริกไทยดำ หอมใหญ่', 'แซลมอน พริกไทยดำ หอมใหญ่ พริกหวาน', [410, 29, 22, 20, 4, 220]],
  ['243', 'กะพง', 'ปลากะพงราดพริก', 'fish', 'เผ็ดกลาง', [], 'ปลากะพงทอดน้ำมันน้อย ราดซอสพริก', 'ปลากะพง พริก กระเทียม มะเขือเทศ', [360, 28, 26, 14, 6, 240]],
  ['290', 'กุ้งเกลือ', 'กุ้งผัดพริกเกลือ', 'seafood', 'เผ็ดน้อย', ['seafood'], 'กุ้งผัดพริกเกลือ หอมกระเทียม', 'กุ้ง พริก เกลือ กระเทียม ข้าวกล้อง', [320, 27, 30, 9, 1, 220]],
  ['291', 'ผัดไทย', 'ผัดไทยกุ้ง', 'seafood', 'ไม่เผ็ด', ['seafood', 'egg', 'peanut'], 'ผัดไทยเส้นจันท์ ซอสมะขามลดน้ำตาล', 'เส้นจันท์ กุ้ง ไข่ ถั่วงอก ถั่วลิสง ซอสมะขาม', [450, 22, 58, 13, 9, 260]],
  ['292', 'วุ้นเส้น', 'ยำวุ้นเส้นกุ้ง', 'seafood', 'เผ็ดมาก', ['seafood'], 'ยำวุ้นเส้นกุ้งรสจัด', 'วุ้นเส้น กุ้ง หมูสับ หอมแดง มะนาว พริก', [300, 20, 36, 7, 4, 240]],
  ['293', 'กะหรี่', 'ทะเลผัดผงกะหรี่', 'seafood', 'ไม่เผ็ด', ['seafood', 'egg', 'milk'], 'ทะเลผัดผงกะหรี่ ไข่นุ่ม', 'กุ้ง ปลาหมึก ไข่ ผงกะหรี่ นมสด', [380, 26, 24, 18, 3, 240]],
  ['310', 'สปาไก่', 'สปาเก็ตตี้โฮลวีทไก่กระเทียม', 'pasta', 'เผ็ดน้อย', ['wheat'], 'สปาเก็ตตี้โฮลวีทผัดไก่กระเทียมพริกแห้ง', 'เส้นโฮลวีท อกไก่ กระเทียม พริกแห้ง น้ำมันมะกอก', [430, 30, 50, 11, 3, 240]],
  ['311', 'สปากุ้ง', 'สปาเก็ตตี้กุ้ง', 'pasta', 'ไม่เผ็ด', ['wheat', 'seafood'], 'สปาเก็ตตี้โฮลวีทกุ้งซอสมะเขือเทศ', 'เส้นโฮลวีท กุ้ง ซอสมะเขือเทศ โหระพา', [440, 26, 54, 12, 7, 250]],
  ['312', 'ขี้เมา', 'เส้นบุกผัดขี้เมาไก่', 'pasta', 'เผ็ดมาก', ['soy'], 'เส้นบุกผัดขี้เมาไก่ แคลอรีต่ำ', 'เส้นบุก อกไก่ พริก ใบกะเพรา กระเทียม', [210, 26, 10, 7, 2, 240]],
  ['313', 'ซีอิ๊ว', 'ผัดซีอิ๊วเส้นบุกหมู', 'pasta', 'ไม่เผ็ด', ['soy', 'egg'], 'ผัดซีอิ๊วเส้นบุกกับหมูและคะน้า', 'เส้นบุก หมู คะน้า ไข่ ซีอิ๊วดำ', [240, 24, 14, 9, 4, 240]],
  ['260', 'ผัก', 'ผัดผักรวมเต้าหู้', 'other', 'ไม่เผ็ด', ['vegan', 'soy'], 'ผักรวมผัดเต้าหู้แข็ง', 'เต้าหู้แข็ง บรอกโคลี แครอท เห็ด ซอสเห็ดหอม', [260, 18, 26, 9, 4, 230]],
  ['261', 'เขียวเจ', 'แกงเขียวหวานเต้าหู้', 'other', 'เผ็ดกลาง', ['vegan', 'soy'], 'แกงเขียวหวานเต้าหู้ กะทิน้อย', 'เต้าหู้ พริกแกงเขียวหวานเจ กะทิ มะเขือ', [330, 16, 28, 17, 4, 250]],
  ['300', 'บราวนี่', 'บราวนี่คีโต', 'other', null, ['egg', 'milk'], 'บราวนี่คีโตไม่ใส่น้ำตาล ใช้แป้งอัลมอนด์', 'แป้งอัลมอนด์ โกโก้ ไข่ เนย สารให้ความหวานแทนน้ำตาล', [190, 6, 5, 16, null, 60]],
]
/** Dishes that start to be sold in the "drift" scenario (on sale in POS, not in the round). */
const DRIFT_NEW: Seed[] = [
  ['320', 'ข้าวผัด', 'ข้าวผัดต้มยำไก่', 'chicken', 'เผ็ดกลาง', [], 'ข้าวผัดรสต้มยำ', 'ข้าวกล้อง อกไก่ ตะไคร้ ใบมะกรูด', [400, 28, 46, 10, 3, 250]],
  ['321', 'แกงส้ม', 'แกงส้มปลาดอลลี่', 'fish', 'เผ็ดกลาง', ['seafood'], 'แกงส้มผักรวมกับปลาดอลลี่', 'ปลาดอลลี่ พริกแกงส้ม ผักรวม', [230, 24, 18, 6, 5, 280]],
]

function dish(s: Seed, i: number): LiveDish {
  const [id, code, name, kind, spicy, flags, desc, ingr, n] = s
  const values: DishValues = {
    description: desc,
    ingredient: ingr,
    kcal: n[0],
    protein: n[1],
    carb: n[2],
    fat: n[3],
    sugar: n[4],
    weight: n[5],
  }
  return {
    id,
    code,
    name,
    kind,
    kind_label: KIND_LABEL[kind] ?? null,
    // two dishes without a POS photo (neutral box)
    img: id === '206' || id === '313' ? null : plate(kind, i),
    values,
    flags,
    spicy,
  }
}

export function mockDishes(): LiveDish[] {
  return SEEDS.map(dish)
}

interface Stored {
  id: string
  status: MenuRoundRowStatus
  valid_from: string
  valid_to: string
  dishes: LiveDish[]
  overrides: OverrideMap
  notes: MenuNotes
  poster_media_id: string | null
  created_by: string | null
  created_by_name: string | null
  created_at: string | null
  approved_by_name: string | null
  approved_at: string | null
  pending: MenuPending | null
}

const clone = <T>(v: T): T => structuredClone(v)
const EMPTY_NOTES: MenuNotes = { new_ids: [], recommended_ids: [], text: '' }

function readStorage(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function mockRole(): KbRole {
  const v = readStorage(MENU_ROLE_KEY)
  return v === 'owner' || v === 'head' || v === 'staff' ? v : 'owner'
}

function storageScenario(): MenuScenario {
  const v = readStorage(MENU_MOCK_KEY) as MenuScenario | null
  return v && SCENARIOS.includes(v) ? v : 'none'
}

/** The last mock built (the one the page uses) — the chat mock asks it which poster /menu should send. */
let latest: MockMenuRoundApi | null = null

export class MockMenuRoundApi implements MenuRoundApi {
  rounds: Stored[] = []
  live: LiveDish[] = []
  posDown = false
  /** tests: a fixed role instead of localStorage */
  role: KbRole | null = null
  scenario: MenuScenario | null = null
  readonly today: string
  private readonly fixedScenario: boolean
  private readonly latency: number
  private readonly media: MediaAsset[]
  private nextId = 10
  private readonly nowMs: () => number

  constructor(opts: { latencyMs?: number; today?: string; scenario?: MenuScenario; now?: () => number } = {}) {
    this.nowMs = opts.now ?? (() => Date.now())
    this.today = opts.today ?? bangkokToday(this.nowMs())
    this.latency = opts.latencyMs ?? 150
    this.media = createMockMedia(Date.parse(`${this.today}T05:00:00Z`))
    this.fixedScenario = opts.scenario !== undefined
    this.seed(opts.scenario ?? storageScenario())
    latest = this
  }

  private async delay(): Promise<void> {
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency))
  }

  private me(): MenuRoundState['me'] {
    const role = this.role ?? mockRole()
    return { ...PEOPLE[role], role, can_edit: role !== 'staff', can_approve: role === 'owner' }
  }

  /** Re-seed when the localStorage scenario changed since the last call. */
  sync(): void {
    if (this.fixedScenario) return
    const s = storageScenario()
    if (s !== this.scenario) this.seed(s)
  }

  private iso(ymd: string, hhmm: string): string {
    // Bangkok wall time → ISO
    return new Date(Date.parse(`${ymd}T${hhmm}:00+07:00`)).toISOString()
  }

  private round(p: Partial<Stored> & Pick<Stored, 'status' | 'valid_from' | 'valid_to'>): Stored {
    const approved = p.status !== 'pending_approval' && p.status !== 'rejected'
    return {
      id: String(this.nextId++),
      dishes: mockDishes(),
      overrides: {},
      notes: clone(EMPTY_NOTES),
      poster_media_id: null,
      created_by: PEOPLE.owner.id,
      created_by_name: PEOPLE.owner.name,
      created_at: this.iso(p.valid_from, '21:30'),
      approved_by_name: approved ? PEOPLE.owner.name : null,
      approved_at: approved ? this.iso(p.valid_from, '21:40') : null,
      pending: null,
      ...p,
    }
  }

  /** The owner's corrections seeded into an approved round (kcal fixed, sugar hidden, a text fixed). */
  private seededOverrides(): OverrideMap {
    return {
      '201': { kcal: 210 },
      '204': { sugar: null },
      '240': { description: 'ปลาดอลลี่นึ่งมะนาว น้ำยำเปรี้ยวจี๊ด เสิร์ฟกับผักกาดขาวและแครอท' },
    }
  }

  private seededNotes(): MenuNotes {
    return {
      new_ids: ['205', '242', '312'],
      recommended_ids: ['201', '237', '240', '260'],
      text: 'เมนูใหม่ 3 เมนูเริ่มขายรอบนี้ ถ้าลูกค้าอยากได้เมนูไม่เผ็ด แนะนำกลุ่มปลาและอกไก่ย่างก่อน',
    }
  }

  seed(s: MenuScenario): void {
    this.scenario = s
    this.nextId = 10
    this.posDown = s === 'posdown'
    this.live = mockDishes()
    const T = this.today
    /** the round before the one in use: ends the day before `from` */
    const prevBefore = (from: string): Stored =>
      this.round({
        status: 'superseded',
        valid_from: addMonthsYmd(addDaysYmd(from, -1), -2),
        valid_to: addDaysYmd(from, -1),
        dishes: mockDishes().filter((d) => !['205', '242', '312'].includes(d.id)),
      })
    const approved = (from: string, to: string): Stored =>
      this.round({
        status: 'approved',
        valid_from: from,
        valid_to: to,
        overrides: this.seededOverrides(),
        notes: this.seededNotes(),
        poster_media_id: MOCK_POSTER_ID,
      })
    switch (s) {
      case 'none':
        this.rounds = []
        break
      case 'active':
      case 'posdown':
        this.rounds = [prevBefore(T), approved(T, addMonthsYmd(T, 2))]
        break
      case 'drift': {
        const from = addDaysYmd(T, -20)
        this.rounds = [prevBefore(from), approved(from, addMonthsYmd(from, 2))]
        // POS now: 2 new dishes, 1 no longer sold, 1 renamed, 2 with edited numbers
        this.live = [
          ...mockDishes()
            .filter((d) => d.id !== '209')
            .map((d) => {
              if (d.id === '251') return { ...d, name: 'หมูผัดพริกหยวกกระเทียม' }
              if (d.id === '181') return { ...d, values: { ...d.values, kcal: 360 } }
              if (d.id === '291') return { ...d, values: { ...d.values, sugar: 7, carb: 55 } }
              return d
            }),
          ...DRIFT_NEW.map((x, i) => dish(x, i + 40)),
        ]
        break
      }
      case 'expiring':
        this.rounds = [prevBefore(addDaysYmd(T, -56)), approved(addDaysYmd(T, -56), addDaysYmd(T, 5))]
        break
      case 'expired':
        this.rounds = [prevBefore(addDaysYmd(T, -64)), approved(addDaysYmd(T, -64), addDaysYmd(T, -3))]
        break
      case 'proposal':
        this.rounds = [
          this.round({
            status: 'pending_approval',
            valid_from: T,
            valid_to: addMonthsYmd(T, 2),
            overrides: { '201': { kcal: 210 }, '204': { sugar: null } },
            notes: this.seededNotes(),
            poster_media_id: MOCK_POSTER_ID,
            created_by: PEOPLE.head.id,
            created_by_name: PEOPLE.head.name,
            created_at: new Date(this.nowMs() - 25 * 60000).toISOString(),
          }),
        ]
        break
    }
  }

  // ---------------------------------------------------------------- reading

  private current(): Stored | null {
    return this.rounds.find((r) => r.status === 'approved') ?? null
  }

  private proposal(): Stored | null {
    return this.rounds.find((r) => r.status === 'pending_approval') ?? null
  }

  private toRound(r: Stored): MenuRound {
    const dishes = r.dishes.map((d) => toRoundDish(d, r.overrides[d.id]))
    return {
      id: r.id,
      status: r.status,
      valid_from: r.valid_from,
      valid_to: r.valid_to,
      dishes,
      dish_count: dishes.length,
      notes: clone(r.notes),
      poster_media_id: r.poster_media_id,
      poster: this.media.find((m) => m.id === r.poster_media_id) ?? null,
      created_by: r.created_by,
      created_by_name: r.created_by_name,
      created_at: r.created_at,
      approved_by_name: r.approved_by_name,
      approved_at: r.approved_at,
      pending: r.pending ? clone(r.pending) : null,
    }
  }

  private status(cur: Stored | null): { status: MenuRoundStatus; days_left: number | null } {
    if (!cur) return { status: 'none', days_left: null }
    if (this.today < cur.valid_from) return { status: 'upcoming', days_left: null }
    if (this.today > cur.valid_to) return { status: 'expired', days_left: null }
    const left = diffDays(this.today, cur.valid_to)
    return { status: left <= 7 ? 'expiring' : 'active', days_left: left }
  }

  private drift(cur: Stored): MenuDrift {
    const inRound = new Map(cur.dishes.map((d) => [d.id, d]))
    const onSale = new Map(this.live.map((d) => [d.id, d]))
    const label = (k: string) => DEFAULT_FIELDS.find((f) => f.key === k)?.label ?? k
    const added = this.live.filter((d) => !inRound.has(d.id)).map((d) => ({ id: d.id, name: d.name }))
    const removed = cur.dishes.filter((d) => !onSale.has(d.id)).map((d) => ({ id: d.id, name: d.name }))
    const renamed: MenuDrift['renamed'] = []
    const changed: MenuDrift['changed'] = []
    for (const d of cur.dishes) {
      const now = onSale.get(d.id)
      if (!now) continue
      if (now.name !== d.name) renamed.push({ id: d.id, from: d.name, to: now.name })
      const fields = FIELD_KEYS.filter((k) => now.values[k] !== d.values[k]).map(label)
      if (fields.length) changed.push({ id: d.id, name: now.name, fields })
    }
    return { count: added.length + removed.length + renamed.length, added, removed, renamed, changed }
  }

  private alertFor(st: MenuRoundStatus, daysLeft: number | null, drift: MenuDrift | null, hasProposal: boolean): MenuAlert | null {
    if (st === 'expired') return { level: 'crit', code: 'expired', text: 'เมนูรอบนี้หมดอายุแล้ว AI ไม่บอกรายละเอียดเมนูจนกว่าจะอนุมัติรอบใหม่' }
    if (drift && drift.count > 0) return { level: 'warn', code: 'drift', text: `เมนูใน POS เปลี่ยนจากรอบที่อนุมัติ ${drift.count} รายการ ตรวจแล้วอนุมัติใหม่` }
    if (st === 'expiring') return { level: 'warn', code: 'expiring', text: `เมนูรอบนี้จะหมดอายุในอีก ${daysLeft ?? 0} วัน เตรียมตรวจและอนุมัติรอบใหม่` }
    if (st === 'none') {
      return {
        level: 'warn',
        code: 'none',
        text: hasProposal
          ? 'ยังไม่มีเมนูรอบนี้ที่อนุมัติ มีเมนูที่ผู้จัดการเสนอรออนุมัติ'
          : 'ยังไม่มีเมนูรอบนี้ที่อนุมัติ AI จึงยังไม่บอกรายละเอียดเมนูให้ลูกค้า',
      }
    }
    return null
  }

  private state(): MenuRoundState {
    const me = this.me()
    const cur = this.current()
    const prop = this.proposal()
    const { status, days_left } = this.status(cur)
    const drift = cur && !this.posDown ? this.drift(cur) : null
    const T = this.today
    return {
      me,
      today: T,
      status,
      days_left,
      current: cur ? this.toRound(cur) : null,
      proposal: prop ? this.toRound(prop) : null,
      live: this.posDown
        ? { ok: false, stale: false, error: 'ต่อ POS ไม่ได้', fetched_at: null, dishes: [] }
        : { ok: true, stale: false, error: null, fetched_at: new Date(this.nowMs()).toISOString(), dishes: clone(this.live) },
      drift,
      alert: me.role === 'owner' ? this.alertFor(status, days_left, drift, !!prop) : null,
      default_period: { from: T, to: addMonthsYmd(T, 2) },
      fields: clone(DEFAULT_FIELDS),
      flag_labels: { ...FLAG_LABELS },
      limits: { text_chars: 600, notes_chars: 300, max_days: 120 },
      history: this.rounds
        .filter((r) => r !== cur && r !== prop)
        .sort((a, b) => Number(b.id) - Number(a.id))
        .map((r) => ({
          id: r.id,
          status: r.status,
          valid_from: r.valid_from,
          valid_to: r.valid_to,
          dish_count: r.dishes.length,
          approved_by_name: r.approved_by_name,
          approved_at: r.approved_at,
        })),
    }
  }

  /** The poster /menu should send now (an approved round in use with a poster), for the chat mock. */
  sendPosterId(): string | null {
    this.sync()
    const cur = this.current()
    const { status } = this.status(cur)
    return cur && (status === 'active' || status === 'expiring') ? cur.poster_media_id : null
  }

  // ---------------------------------------------------------------- writing

  private find(id: string): Stored {
    const r = this.rounds.find((x) => x.id === id)
    if (!r) throw new ApiError(404, 'ไม่พบรอบเมนูนี้')
    return r
  }

  private checkBody(b: EditBody, from: string, to: string): void {
    const problem = periodProblem(from, to, 120)
    if (problem) throw new ApiError(422, problem)
    if (b.notes && b.notes.text.length > 300) throw new ApiError(422, 'หมายเหตุถึง AI ยาวเกิน 300 ตัวอักษร')
    for (const o of Object.values(b.overrides ?? {})) {
      for (const [k, v] of Object.entries(o)) {
        if (typeof v === 'string' && v.length > 600) throw new ApiError(422, 'ข้อความยาวเกิน 600 ตัวอักษร')
        if (k !== HIDE_DISH && !FIELD_KEYS.includes(k as never)) throw new ApiError(422, 'ช่องข้อมูลไม่ถูกต้อง')
      }
    }
  }

  private cleanMap(map: OverrideMap, dishes: LiveDish[]): OverrideMap {
    const ids = new Set(dishes.map((d) => d.id))
    const out: OverrideMap = {}
    for (const [id, o] of Object.entries(map)) if (ids.has(id) && Object.keys(o).length) out[id] = { ...o }
    return out
  }

  private apply(r: Stored, b: EditBody): void {
    if (b.overrides) r.overrides = this.cleanMap(b.overrides, r.dishes)
    if (b.notes) r.notes = clone(b.notes)
    if (b.poster_media_id !== undefined) r.poster_media_id = b.poster_media_id
    if (b.valid_from) r.valid_from = b.valid_from
    if (b.valid_to) r.valid_to = b.valid_to
  }

  private approveNow(r: Stored): void {
    const cur = this.current()
    if (cur && cur !== r) cur.status = 'superseded'
    r.status = 'approved'
    r.approved_by_name = PEOPLE.owner.name
    r.approved_at = new Date(this.nowMs()).toISOString()
  }

  /** `fresh` (re-read POS now) makes no difference here: the mock's POS list is always "now". */
  async load(_opts: { fresh?: boolean } = {}): Promise<MenuRoundState> {
    this.sync()
    await this.delay()
    return this.state()
  }

  async alert(): Promise<MenuAlert | null> {
    this.sync()
    await this.delay()
    return this.state().alert
  }

  async create(body: NewRoundBody): Promise<MenuRoundState> {
    this.sync()
    await this.delay()
    const me = this.me()
    if (!me.can_edit) throw new ApiError(403, 'ไม่มีสิทธิ์ทำรายการนี้')
    if (this.posDown) throw new ApiError(503, 'ต่อ POS ไม่ได้ ลองใหม่อีกครั้ง')
    const liveIds = this.live.map((d) => d.id).sort().join()
    if ([...body.expect_ids].sort().join() !== liveIds) throw new ApiError(409, 'เมนูใน POS เปลี่ยนระหว่างตรวจ กรุณาตรวจอีกครั้ง')
    this.checkBody(body, body.valid_from, body.valid_to)
    const r = this.round({
      status: 'pending_approval',
      valid_from: body.valid_from,
      valid_to: body.valid_to,
      dishes: clone(this.live),
      notes: clone(body.notes),
      poster_media_id: body.poster_media_id,
      created_by: me.id,
      created_by_name: me.name,
      created_at: new Date(this.nowMs()).toISOString(),
      approved_by_name: null,
      approved_at: null,
    })
    r.overrides = this.cleanMap(body.overrides, r.dishes)
    if (me.role === 'owner') this.approveNow(r)
    else this.rounds = this.rounds.filter((x) => x.status !== 'pending_approval') // a newer proposal replaces it
    this.rounds.push(r)
    return this.state()
  }

  async edit(id: string, body: EditBody): Promise<MenuRoundState> {
    this.sync()
    await this.delay()
    const me = this.me()
    if (!me.can_edit) throw new ApiError(403, 'ไม่มีสิทธิ์ทำรายการนี้')
    const r = this.find(id)
    if (r.status !== 'approved' && r.status !== 'pending_approval') throw new ApiError(409, 'รอบนี้ไม่ได้ใช้แล้ว')
    this.checkBody(body, body.valid_from ?? r.valid_from, body.valid_to ?? r.valid_to)
    if (me.role === 'owner' || r.status === 'pending_approval') {
      if (me.role !== 'owner' && r.created_by !== me.id) throw new ApiError(403, 'แก้ได้เฉพาะข้อเสนอของตัวเอง')
      this.apply(r, body)
      return this.state()
    }
    // a head on the approved round: waits for the owner, the AI keeps the approved version
    const p: MenuPending = { ...(r.pending ?? {}), by: me.id, by_name: me.name, at: new Date(this.nowMs()).toISOString() }
    if (body.overrides) p.overrides = this.cleanMap(body.overrides, r.dishes)
    if (body.notes) p.notes = clone(body.notes)
    if (body.poster_media_id !== undefined) p.poster_media_id = body.poster_media_id
    if (body.valid_from) p.valid_from = body.valid_from
    if (body.valid_to) p.valid_to = body.valid_to
    r.pending = p
    return this.state()
  }

  async act(id: string, action: MenuAction): Promise<MenuRoundState> {
    this.sync()
    await this.delay()
    const me = this.me()
    const r = this.find(id)
    const owner = me.role === 'owner'
    if (action === 'approve') {
      if (!owner) throw new ApiError(403, 'เฉพาะเจ้าของอนุมัติได้')
      if (r.status === 'pending_approval') this.approveNow(r)
      else if (r.status === 'approved' && r.pending) {
        const { by: _by, by_name: _n, at: _at, ...changes } = r.pending
        this.apply(r, changes)
        r.pending = null
      } else throw new ApiError(409, 'ไม่มีอะไรรออนุมัติ')
      return this.state()
    }
    if (action === 'reject') {
      if (r.status === 'pending_approval') {
        if (!owner && r.created_by !== me.id) throw new ApiError(403, 'ไม่มีสิทธิ์ทำรายการนี้')
        r.status = 'rejected'
      } else if (r.status === 'approved' && r.pending) {
        if (!owner && r.pending.by !== me.id) throw new ApiError(403, 'ไม่มีสิทธิ์ทำรายการนี้')
        r.pending = null
      } else throw new ApiError(409, 'ไม่มีอะไรรออนุมัติ')
      return this.state()
    }
    if (!owner) throw new ApiError(403, 'เฉพาะเจ้าของปิดใช้ได้')
    if (r.status !== 'approved') throw new ApiError(409, 'รอบนี้ไม่ได้ใช้อยู่')
    r.status = 'retired'
    r.pending = null
    return this.state()
  }
}

/** For the chat mock's GET /canned: /menu sends the round's poster while a round with a poster is in use. */
export function mockMenuSendMedia(media: MediaAsset[]): MediaAsset[] | null {
  const m = latest ?? new MockMenuRoundApi({ latencyMs: 0 })
  const id = m.sendPosterId()
  const asset = id ? media.find((x) => x.id === id && !x.deleted) : null
  return asset ? [asset] : null
}
